/**
 * SNAPDEBUG.JSX - Outil de Snapshot pour capturer toutes les valeurs de la scène
 * 
 * Ce composant permet de capturer en un clic toutes les valeurs importantes
 * de la scène : caméra, lumières, positions des objets.
 * 
 * FONCTIONNALITÉS :
 * - Bouton Snap All : capture toutes les valeurs actuelles
 * - Copie automatique dans le clipboard (optionnel)
 * - Affichage formaté dans la console
 * 
 * UTILISATION :
 * Manipulez la scène avec OrbitControls et les contrôles Leva,
 * puis cliquez sur "Snap All" pour récupérer toutes les valeurs.
 */

import { useCallback } from 'react'
import { useControls, button } from 'leva'
import { useThree } from '@react-three/fiber'

function SnapDebug() {
  const { camera, controls, scene } = useThree()

  // Fonction pour trouver un objet dans la scène par nom
  const findObjectByName = useCallback((name) => {
    return scene.getObjectByName(name)
  }, [scene])

  // Fonction pour capturer toutes les valeurs
  const snapAllValues = useCallback(() => {
    console.log('📸 SNAPSHOT COMPLET DE LA SCÈNE')
    console.log('=' .repeat(60))
    
    // Caméra
    console.log('\n📷 CAMÉRA :')
    console.log('  Position :')
    console.log(`    x: ${camera.position.x.toFixed(2)}`)
    console.log(`    y: ${camera.position.y.toFixed(2)}`)
    console.log(`    z: ${camera.position.z.toFixed(2)}`)
    if (controls) {
      console.log('  Target OrbitControls :')
      console.log(`    x: ${controls.target.x.toFixed(2)}`)
      console.log(`    y: ${controls.target.y.toFixed(2)}`)
      console.log(`    z: ${controls.target.z.toFixed(2)}`)
    }
    console.log(`  FOV: ${camera.fov}°`)
    console.log(`  Near: ${camera.near}`)
    console.log(`  Far: ${camera.far}`)

    // Lumières
    console.log('\n💡 LUMIÈRES :')
    const lights = []
    scene.traverse((object) => {
      if (object.isAmbientLight) {
        console.log('  Lumière Ambiante :')
        console.log(`    intensity: ${object.intensity}`)
        console.log(`    color: #${object.color.getHexString()}`)
      } else if (object.isDirectionalLight) {
        lights.push(object)
      }
    })
    
    lights.forEach((light, index) => {
      console.log(`  Lumière Directionnelle ${index + 1} :`)
      console.log(`    position: [${light.position.x.toFixed(1)}, ${light.position.y.toFixed(1)}, ${light.position.z.toFixed(1)}]`)
      console.log(`    intensity: ${light.intensity}`)
      console.log(`    color: #${light.color.getHexString()}`)
    })

    // Objets 3D
    
    // On parcourt la scène pour trouver les meshes principaux
    const meshes = []
    scene.traverse((object) => {
      if (object.isMesh && object.parent && object.parent.type === 'Group') {
        const parent = object.parent
        if (parent.parent === scene) {
          // C'est un groupe racine (probablement Radio ou Sausage)
          if (!meshes.find(m => m === parent)) {
            meshes.push(parent)
          }
        }
      }
    })

    meshes.forEach((obj) => {
      console.log(`  Objet : ${obj.name || 'Unnamed'}`)
      console.log(`    position: [${obj.position.x.toFixed(2)}, ${obj.position.y.toFixed(2)}, ${obj.position.z.toFixed(2)}]`)
      console.log(`    rotation: [${obj.rotation.x.toFixed(3)}, ${obj.rotation.y.toFixed(3)}, ${obj.rotation.z.toFixed(3)}]`)
      console.log(`    scale: [${obj.scale.x.toFixed(2)}, ${obj.scale.y.toFixed(2)}, ${obj.scale.z.toFixed(2)}]`)
    })

    // Code formaté pour copier-coller
    console.log('\n📋 CODE À COPIER :')
    console.log('=' .repeat(60))
    console.log('\n// Caméra')
    console.log(`camera: {`)
    console.log(`  position: [${camera.position.x.toFixed(1)}, ${camera.position.y.toFixed(1)}, ${camera.position.z.toFixed(1)}],`)
    if (controls) {
      console.log(`  target: [${controls.target.x.toFixed(1)}, ${controls.target.y.toFixed(1)}, ${controls.target.z.toFixed(1)}],`)
    }
    console.log(`  fov: ${camera.fov},`)
    console.log(`  near: ${camera.near},`)
    console.log(`  far: ${camera.far}`)
    console.log(`}`)

    // Copier dans le clipboard
    const clipboardText = `
// Snapshot de la scène - ${new Date().toLocaleString()}

// Caméra
camera: {
  position: [${camera.position.x.toFixed(1)}, ${camera.position.y.toFixed(1)}, ${camera.position.z.toFixed(1)}],
  target: [${controls?.target.x.toFixed(1)}, ${controls?.target.y.toFixed(1)}, ${controls?.target.z.toFixed(1)}],
  fov: ${camera.fov},
  near: ${camera.near},
  far: ${camera.far}
}
`
    
    // Tentative de copie dans le clipboard (peut échouer selon les permissions)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(clipboardText)
        .then(() => {
          console.log('\n✅ Valeurs copiées dans le clipboard !')
        })
        .catch((err) => {
          console.log('\n⚠️ Impossible de copier dans le clipboard automatiquement')
          console.log('Vous pouvez copier les valeurs ci-dessus manuellement.')
        })
    }

    console.log('\n' + '='.repeat(60))
  }, [camera, controls, scene])

  // Fonction pour capturer uniquement la caméra
  const snapCamera = useCallback(() => {
    console.log('📷 SNAPSHOT CAMÉRA')
    console.log('  Position : [' + 
      `${camera.position.x.toFixed(2)}, ` +
      `${camera.position.y.toFixed(2)}, ` +
      `${camera.position.z.toFixed(2)}]`
    )
    if (controls) {
      console.log('  Target : [' + 
        `${controls.target.x.toFixed(2)}, ` +
        `${controls.target.y.toFixed(2)}, ` +
        `${controls.target.z.toFixed(2)}]`
      )
    }
    console.log(`  FOV: ${camera.fov}°`)
  }, [camera, controls])

  // Contrôles Leva
  useControls('📸 Snap', {
    '---1': { value: '--- Captures Rapides ---', editable: false },
    snapAll: button(() => snapAllValues(), {
      label: '📸 Snap All (Tout capturer)'
    }),
    snapCamera: button(() => snapCamera(), {
      label: '📷 Snap Camera'
    }),
    '---2': { value: '--- Info ---', editable: false },
    info: {
      value: 'Manipulez la scène avec OrbitControls, puis cliquez sur Snap All pour récupérer toutes les valeurs.',
      editable: false
    }
  }, { collapsed: true })

  // Ce composant ne rend rien visuellement
  return null
}

export default SnapDebug

